import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { organizationApi } from '../../services/api'
import {
  AdvancedDisclosure,
  Button,
  FormField,
  FormLayoutActions,
  FormLayoutSection,
  ModalShell,
  StatePanel,
  Toggle,
} from '../../components/common'
import styles from '../SettingsPage.module.css'

const tierOptions = [
  { value: '', label: 'No cap' },
  { value: 'T3', label: 'T3' },
  { value: 'T2', label: 'T2' },
  { value: 'T1', label: 'T1' },
  { value: 'T0', label: 'T0' },
  { value: 'excluded', label: 'Excluded' },
]

const toDraft = (settings) => ({
  kill_switch: settings.kill_switch,
  max_tier: settings.max_tier,
  profile_weight: String(settings.profile_weight),
})

export function WISettingsSection({ orgId }) {
  const [baseline, setBaseline] = useState(null)
  const [draft, setDraft] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [pendingPatch, setPendingPatch] = useState(null)

  const load = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    setError(null)
    try {
      const settings = await organizationApi.getWISettings(orgId)
      setBaseline(settings)
      setDraft(toDraft(settings))
    } catch (loadError) {
      const message = loadError.response?.data?.error || 'Failed to load Wager Intelligence settings'
      setError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => { load() }, [load])

  const patch = {}
  if (draft && baseline) {
    if (draft.kill_switch !== baseline.kill_switch) patch.kill_switch = draft.kill_switch
    if (draft.max_tier !== baseline.max_tier) patch.max_tier = draft.max_tier
    if (Number(draft.profile_weight) !== baseline.profile_weight) patch.profile_weight = Number(draft.profile_weight)
  }
  const dirty = Object.keys(patch).length > 0
  const validWeight = draft && draft.profile_weight !== '' && Number.isFinite(Number(draft.profile_weight))
    && Number(draft.profile_weight) >= 0 && Number(draft.profile_weight) <= 1

  const save = async (changes) => {
    setSaving(true)
    try {
      const settings = await organizationApi.updateWISettings(orgId, changes)
      setBaseline(settings)
      setDraft(toDraft(settings))
      setPendingPatch(null)
      toast.success('Wager Intelligence settings saved')
    } catch (saveError) {
      toast.error(saveError.response?.data?.error || 'Failed to save Wager Intelligence settings')
    } finally {
      setSaving(false)
    }
  }

  const submit = (event) => {
    event.preventDefault()
    if (!dirty || !validWeight || saving) return
    if (patch.kill_switch === true || ['excluded', 'T0'].includes(patch.max_tier)) {
      setPendingPatch(patch)
    } else {
      save(patch)
    }
  }

  const actor = baseline?.updated_by
  const actorName = actor?.name || actor?.email || actor?.id

  return (
    <FormLayoutSection title="Wager Intelligence" description="Controls Wager Intelligence decisions for this organization.">
      {loading ? (
        <StatePanel state="loading" title="Loading Wager Intelligence" compact />
      ) : error ? (
        <StatePanel state="error" title="Wager Intelligence is unavailable" description={error} onRetry={load} compact />
      ) : draft && (
        <form className={styles.formStack} onSubmit={submit}>
          <Toggle
            label="Personalization"
            checked={!draft.kill_switch}
            onChange={(enabled) => setDraft((current) => ({ ...current, kill_switch: !enabled }))}
            disabled={saving}
          />
          <FormField
            label="Maximum tier"
            type="select"
            options={tierOptions}
            value={draft.max_tier}
            onChange={(event) => setDraft((current) => ({ ...current, max_tier: event.target.value }))}
            disabled={saving}
          />
          <AdvancedDisclosure>
            <FormField
              label="Profile weight"
              type="number"
              min={0}
              max={1}
              step="any"
              required
              value={draft.profile_weight}
              onChange={(event) => setDraft((current) => ({ ...current, profile_weight: event.target.value }))}
              error={!validWeight ? 'Enter a number from 0 to 1.' : undefined}
              disabled={saving}
            />
          </AdvancedDisclosure>
          {actorName && baseline.updated_at && (
            <p className={styles.settingsHelp}>Last changed by {actorName} at {new Date(baseline.updated_at).toLocaleString()}</p>
          )}
          <FormLayoutActions>
            <Button type="submit" disabled={saving || !dirty || !validWeight}>Save Wager Intelligence</Button>
          </FormLayoutActions>
        </form>
      )}
      <ModalShell
        open={Boolean(pendingPatch)}
        title="Confirm Wager Intelligence changes"
        onClose={() => { if (!saving) setPendingPatch(null) }}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setPendingPatch(null)} disabled={saving}>Cancel</Button>
            <Button variant="danger" onClick={() => save(pendingPatch)} disabled={saving}>Confirm changes</Button>
          </>
        )}
      >
        {pendingPatch?.kill_switch === true && <p>Personalization will be turned off for this organization.</p>}
        {pendingPatch?.max_tier !== undefined && <p>Maximum tier will be set to {tierOptions.find((option) => option.value === pendingPatch.max_tier)?.label}.</p>}
        {pendingPatch?.profile_weight !== undefined && <p>Profile weight will be set to {pendingPatch.profile_weight}.</p>}
      </ModalShell>
    </FormLayoutSection>
  )
}
